import { css } from '@emotion/react';
import { useRouter } from 'next/router';
import { MouseEvent, useEffect } from 'react';

import { Colors, Link, Typography } from '@zapier/design-system';
import { urls, useSize } from '@zapier/identity-utils';
import { logout } from '@zapier/authentication/src/utils/logout';
import { useResendSignupVerification } from '@zapier/authentication/src/hooks/useResendSignupVerification';
import { useTranslations } from 'next-intl';

import {
  AuthLayout,
  CompaniesIntroPromo,
} from '@zapier/auth-pages/src/components/global';

const textStyle = css`
  ${Typography.Body1}
  color: ${Colors.GrayWarm10};
  text-align: center;
`;

const rootStyle = css`
  display: grid;
  gap: 25px;
`;

const linkRowStyle = css`
  ${Typography.Body1}
  text-align: center;
`;

function Bold(chunks: React.ReactNode) {
  return <b>{chunks}</b>;
}

export function VerifyEmailPage() {
  const router = useRouter();
  const size = useSize();
  const t = useTranslations('verify-email');
  const { status: resendStatus, resend } = useResendSignupVerification();

  const email = (router.query.email as string | undefined) ?? '';

  // The page is statically pre-rendered, so on the first client render
  // `router.query` is empty even when the URL has `?email=...`. Wait for
  // `router.isReady` before deciding the email is missing.
  const shouldRedirect = router.isReady && !email;

  useEffect(() => {
    if (shouldRedirect) {
      void router.replace(urls.LOGIN_URL);
    }
  }, [shouldRedirect, router]);

  function handleResend(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    // Ignore re-clicks while a resend is in flight: each resend mints a fresh
    // token (rotating out the previous one) and sends another email, so an
    // un-gated double-click would deliver duplicate emails and invalidate the
    // link in the earlier one.
    if (resendStatus === 'submitting') {
      return;
    }
    void resend(email);
  }

  function ResendLink(chunks: React.ReactNode) {
    return (
      <Link href="#" onClick={handleResend}>
        {chunks}
      </Link>
    );
  }

  async function handleLogout(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    try {
      await logout();
    } finally {
      router.push(urls.LOGIN_URL);
    }
  }

  if (!router.isReady || !email) {
    return null;
  }

  return (
    <AuthLayout
      promo={<CompaniesIntroPromo />}
      showPromo={size !== 'small'}
      title={t('title')}
      titlePosition="right"
    >
      <section css={rootStyle}>
        <p css={textStyle}>
          {t.rich('description', {
            b: Bold,
            email,
          })}
        </p>
        <p css={textStyle}>
          {resendStatus === 'success'
            ? t('resend-sent')
            : resendStatus === 'submitting'
            ? t('resend-sending')
            : resendStatus === 'error'
            ? t.rich('resend-error', {
                resend: ResendLink,
              })
            : t.rich('resend-prompt', {
                resend: ResendLink,
              })}
        </p>
        <p css={linkRowStyle}>
          <Link href={urls.LOGIN_URL} onClick={handleLogout}>
            {t('back-to-login')}
          </Link>
        </p>
      </section>
    </AuthLayout>
  );
}
